import { Link, useParams } from "react-router-dom";
import { Check } from "lucide-react";
import { useApi } from "@/lib/useApi";
import { api, fcfa, fmtDate, fmtDateTime, imgUrl, ORDER_FLOW, ORDER_STATUS } from "@/lib/api";
import { Loading } from "@/components/RequireAuth";
import { StatusBadge } from "@/components/Badges";
import { OrderThread } from "@/components/OrderThread";
import { WhatsAppOrderButton } from "@/components/WhatsAppOrderButton";

export const OrderTimeline = ({ status }) => {
  const idx = ORDER_FLOW.indexOf(status);
  return (
    <ol data-testid="order-timeline" className="grid grid-cols-4 gap-2">
      {ORDER_FLOW.map((s, i) => (
        <li key={s} className="flex flex-col items-center gap-1 text-center">
          <span className={`grid h-8 w-8 place-items-center rounded-full text-xs font-bold ${i <= idx ? "bg-green-700 text-white" : "bg-gray-200 text-gray-500"}`}>{i <= idx ? <Check className="h-4 w-4" /> : i + 1}</span>
          <span className="text-[11px] font-semibold leading-tight">{ORDER_STATUS[s]}</span>
        </li>
      ))}
    </ol>
  );
};

export const OrderSummary = ({ o }) => (
  <div className="space-y-2">
    {o.items.map((i, k) => (
      <div key={k} className="flex items-center gap-3 text-sm">
        <img src={imgUrl(i.image)} alt="" className="h-12 w-12 rounded-lg object-cover" />
        <span className="flex-1">{i.quantity} × {i.title}{i.variant ? ` (${i.variant})` : ""}</span>
        <span className="tnum font-semibold">{fcfa(i.line_total, o.currency)}</span>
      </div>
    ))}
    <div className="space-y-1 border-t pt-2 text-sm">
      <p className="flex justify-between"><span>Livraison {o.delivery_zone ? `(${o.delivery_zone.name})` : ""}</span><span className="tnum">{fcfa(o.delivery_fee, o.currency)}</span></p>
      <p className="flex justify-between font-bold"><span>Total</span><span className="tnum text-orange-600">{fcfa(o.total, o.currency)}</span></p>
      <p className="text-xs text-gray-500">Paiement à la livraison · statut : non payé</p>
    </div>
  </div>
);

export function MyOrders() {
  const { data } = useApi("/orders/mine");
  return (
    <div data-testid="my-orders-page" className="mx-auto max-w-4xl px-4 py-8 md:px-6">
      <h1 className="mb-6 font-display text-3xl font-black tracking-tight">Mes commandes</h1>
      {!data ? <Loading /> : data.length === 0 ? <p data-testid="orders-empty" className="text-gray-500">Vous n'avez pas encore de commande.</p> : (
        <div className="space-y-3">
          {data.map((o) => (
            <Link key={o.id} to={`/mes-commandes/${o.id}`} data-testid={`order-row-${o.number}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-white p-4 hover:border-orange-300">
              <div><p className="font-display font-bold">{o.number}</p><p className="text-xs text-gray-500">{o.shop_name} · {fmtDate(o.created_at)}</p></div>
              <div className="flex items-center gap-3"><StatusBadge status={o.status} /><span className="tnum font-bold">{fcfa(o.total, o.currency)}</span></div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const { data: o, setData, error } = useApi(`/orders/${id}`);
  if (error) return <p className="p-10 text-center text-gray-500">{error}</p>;
  if (!o) return <Loading />;
  return (
    <div data-testid="order-detail-page" className="mx-auto max-w-4xl space-y-6 px-4 py-8 md:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-3xl font-black">{o.number}</h1><p className="text-sm text-gray-500">{o.shop_name} · {fmtDateTime(o.created_at)}</p></div>
        <StatusBadge status={o.status} testid="order-status" />
      </div>
      <div data-testid="order-whatsapp" className="rounded-2xl border bg-white p-5"><WhatsAppOrderButton o={o} /></div>
      <div className="rounded-2xl border bg-white p-5"><OrderTimeline status={o.status} /></div>
      <div className="grid gap-6 md:grid-cols-2">
        <div className="rounded-2xl border bg-white p-5"><h2 className="mb-3 font-display text-lg font-bold">Récapitulatif</h2><OrderSummary o={o} /></div>
        <div className="rounded-2xl border bg-white p-5 text-sm">
          <h2 className="mb-3 font-display text-lg font-bold">Livraison</h2>
          <p>{o.delivery.name} · {o.delivery.phone}</p><p>{o.delivery.address}, {o.delivery.city}</p>
        </div>
      </div>
      <div className="rounded-2xl border bg-white p-5">
        <h2 className="mb-3 font-display text-lg font-bold">Messages avec le vendeur</h2>
        <OrderThread messages={o.messages} me="client" onSend={async (text) => { const { data } = await api.post(`/orders/${o.id}/messages`, { text }); setData((prev) => ({ ...data, shop_whatsapp: prev.shop_whatsapp })); }} />
      </div>
    </div>
  );
}
